import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useProjects } from '../contexts/ProjectsContext';
import { useFolders } from '../contexts/FoldersContext';
import { useClients } from '../contexts/ClientsContext';
import { saveAnnotationSnapshotV2 } from '../lib/annotationV2Persistence';

function LocalStorageMigration() {
  const [migrationStatus, setMigrationStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [migrationLog, setMigrationLog] = useState<string[]>([]);
  const { addProject } = useProjects();
  const { addFolder } = useFolders();
  const { addClient } = useClients();

  const addLog = (message: string) => {
    setMigrationLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const migrateAnnotations = async () => {
    addLog('Starting annotations migration...');
    
    // Find all localStorage keys that contain annotations
    const annotationKeys = Object.keys(localStorage).filter(key => key.startsWith('annotations_'));
    
    for (const key of annotationKeys) {
      try {
        const estimationId = key.replace('annotations_', '');
        const annotationsData = localStorage.getItem(key);
        
        if (annotationsData && supabase) {
          const annotations = JSON.parse(annotationsData);
          
          // Check if annotations already exist in v2 source-of-truth table
          const { data: existingRows, error: existingError } = await supabase
            .from('annotation_entities_v2')
            .select('annotation_uid')
            .eq('estimation_id', estimationId)
            .is('deleted_at', null)
            .limit(1);
          if (existingError) {
            throw existingError;
          }
          const hasExisting = Array.isArray(existingRows) && existingRows.length > 0;
          
          if (!hasExisting) {
            const normalized = Array.isArray(annotations) ? annotations : [];
            try {
              await saveAnnotationSnapshotV2({
                supabase: supabase as any,
                estimationId,
                activeMode: 'estimate',
                currentDrawingFileId: null,
                annotations: normalized,
              });
              addLog(`Successfully migrated annotations for estimation ${estimationId}`);
              // Remove from localStorage after successful migration
              localStorage.removeItem(key);
            } catch (error: any) {
              addLog(`Error migrating annotations for ${estimationId}: ${error?.message || error}`);
            }
          } else {
            addLog(`Annotations for ${estimationId} already exist in Supabase`);
            // Remove from localStorage since it's already in Supabase
            localStorage.removeItem(key);
          }
        }
      } catch (error) {
        addLog(`Error processing annotations for ${key}: ${error}`);
      }
    }
  };

  const migrateLocalData = async () => {
    try {
      setMigrationStatus('running');
      addLog('Starting local storage migration to Supabase...');

      // Migrate annotations
      await migrateAnnotations();

      // Check for any legacy project data in localStorage
      const legacyProjects = localStorage.getItem('projects');
      if (legacyProjects) {
        addLog('Found legacy projects data in localStorage');
        try {
          const projects = JSON.parse(legacyProjects);
          addLog(`Found ${projects.length} projects to migrate`);
          
          for (const project of projects) {
            if (!project.fromSupabase) {
              await addProject(project);
              addLog(`Migrated project: ${project.name}`);
            }
          }
          
          // Remove legacy data after successful migration
          localStorage.removeItem('projects');
          addLog('Removed legacy projects from localStorage');
        } catch (error) {
          addLog(`Error migrating projects: ${error}`);
        }
      }

      // Check for any legacy folder data
      const legacyFolders = localStorage.getItem('folders');
      if (legacyFolders) {
        addLog('Found legacy folders data in localStorage');
        try {
          const folders = JSON.parse(legacyFolders);
          for (const folder of folders) {
            if (folder.id !== '00000000-0000-0000-0000-000000000001') { // Skip default folder
              await addFolder(folder.name, folder.parentId);
              addLog(`Migrated folder: ${folder.name}`);
            }
          }
          localStorage.removeItem('folders');
        } catch (error) {
          addLog(`Error migrating folders: ${error}`);
        }
      }

      // Check for any legacy client data
      const legacyClients = localStorage.getItem('clients');
      if (legacyClients) {
        addLog('Found legacy clients data in localStorage');
        try {
          const clients = JSON.parse(legacyClients);
          for (const client of clients) {
            await addClient(client.name, client.email, client.phone);
            addLog(`Migrated client: ${client.name}`);
          }
          localStorage.removeItem('clients');
        } catch (error) {
          addLog(`Error migrating clients: ${error}`);
        }
      }

      addLog('Migration completed successfully!');
      setMigrationStatus('completed');
      
      // Mark migration as completed
      localStorage.setItem('supabase_migration_completed', 'true');
    } catch (error) {
      addLog(`Migration failed: ${error}`);
      setMigrationStatus('error');
    }
  };

  useEffect(() => {
    // Check if migration has already been completed
    const migrationCompleted = localStorage.getItem('supabase_migration_completed');
    
    if (!migrationCompleted && supabase) {
      // Start migration automatically
      migrateLocalData();
    } else if (migrationCompleted) {
      setMigrationStatus('completed');
      addLog('Migration already completed');
    }
  }, []);

  if (migrationStatus === 'idle' || migrationStatus === 'completed') {
    return null; // Don't show anything if not migrating or already done
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      right: 20,
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: 8,
      padding: 16,
      maxWidth: 400,
      maxHeight: 300,
      overflow: 'auto',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
    }}>
      <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600 }}>
        {migrationStatus === 'running' ? '🔄 Migrating Local Data...' : '❌ Migration Error'}
      </h3>
      <div style={{ fontSize: 12, fontFamily: 'monospace', lineHeight: 1.5 }}>
        {migrationLog.map((log, index) => (
          <div key={index} style={{ marginBottom: 4 }}>{log}</div>
        ))}
      </div>
    </div>
  );
}

export default LocalStorageMigration;
